import { FC, useEffect, useRef, useState } from 'react';
import './ModalArticleHighlightsInsert.scss';
import ModalDefault from '../../../ModalDefault/ModalDefault';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../../../providers/AuthProvider';
import { set, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Card, Col, FormGroup, Row } from 'react-bootstrap';
import ErrorMessage from '../../../ErrorMessage/ErrorMessage';
import { schema } from './Validate';
import ReactSelect from "react-select";
import { useArticlesHighlights } from '../../../../providers/Articles/ArticlesHighlights/ArticlesHighlightsProvider';
import { useAgentsCategories } from '../../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider';
import { useSections } from '../../../../providers/Articles/Sections/SectionsProvider';
import { useSubsections } from '../../../../providers/Articles/Subsections/SubsectionsProvider';
import { customStyles } from '../../../../models/SelectCustomStyles';
import { useArticles } from '../../../../providers/Articles/ArticlesProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { BvIcon } from 'bevi-icon';

interface ModalArticleHighlightsInsertProps {
  show: boolean;
  onClose: () => void;
}

const ModalArticleHighlightsInsert: FC<ModalArticleHighlightsInsertProps> = ({ show, onClose }) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<any | null>(null);
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [selectedSubsection, setSelectedSubsection] = useState<any | null>(null);
  const [selectedRelatedArticles, setSelectedRelatedArticles] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { categoriesOptions, setParams: setParamsAgentsCategories } = useAgentsCategories();
  const { sectionsOptions, setSectionsOptions, setParams: setParamsSections } = useSections();
  const { subsectionsOptions, setSubsectionsOptions, setParams: setParamsSubsections } = useSubsections();
  const { articlesHighlights, setError, handleList, handleFetch } = useArticlesHighlights();
  const { setParamsArticlesRelated, articlesRelatedOptions, setArticlesRelatedOptions, handleList: handleListArticles } = useArticles();
  const { onInvalid } = useAuth();

  const { handleSubmit, setValue, reset, formState: { errors } } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    setError('');
    setSelectedCategories(null);
    setSelectedSection(null);
    setSelectedSubsection(null);
    setSelectedRelatedArticles([]);
    setArticles([]);
    reset();
    onClose();
  }

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError('');

    const ret = await handleFetch(data as any);

    if (ret) {
      toast.success('Artigos Destaques salvo com sucesso!');
      handleListArticles();
      onClose();
    }

    setIsLoadingSubmit(false);
  }

  useEffect(() => {
    if (show) {
      setParamsAgentsCategories({ withPaginate: false, filter: { only_active: true } });
      handleList();
    }
  }, [show]);

  useEffect(() => {
    setValue('category_id', selectedCategories?.value || null);
    if (selectedCategories) {
      setParamsSections({ withPaginate: false, filter: { only_active: true, category_id: selectedCategories?.value } });
      setParamsArticlesRelated({ withPaginate: false, filter: { only_active: true, category_id: selectedCategories?.value } });
    }
  }, [selectedCategories]);

  useEffect(() => {
    setValue('section_id', selectedSection?.value || null);
    if (selectedSection) {
      setParamsSubsections({ withPaginate: false, filter: { only_active: true, section_id: selectedSection?.value } });
      setParamsArticlesRelated({ withPaginate: false, filter: { only_active: true, section_id: selectedSection?.value } });
    }
  }, [selectedSection]);

  useEffect(() => {
    setValue('subsection_id', selectedSubsection?.value || null);
    if (selectedSubsection) {
      setParamsArticlesRelated({ withPaginate: false, filter: { only_active: true, subsection_id: selectedSubsection?.value } });
    }
  }, [selectedSubsection]);

  useEffect(() => {
    if (show && articlesHighlights?.length > 0) {
      setArticles(articlesHighlights.map(article => ({
        id: article?.article?.id,
        title: article?.article?.title,
        category_name: article?.article?.category?.name
      })));
    }
  }, [show, articlesHighlights]);

  useEffect(() => {
    setValue('highlights', articles.map(article => ({ article_id: article.id })));
    setArticlesRelatedOptions(prev =>
      (prev ?? []).filter(opt => !articles.some(a => a.id === opt.value))
    );
  }, [articles]);

  const handleAddArticle = () => {
    if (selectedRelatedArticles.length === 0) return;

    setArticles(prev => {
      const newArticles = selectedRelatedArticles.filter(
        article => !prev.some(existing => existing.id === article.value)
      );

      if (newArticles.length === 0) return prev;

      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });

      return [
        ...prev,
        ...newArticles.map(article => ({
          id: article.value,
          title: article.label,
          category_name: article.category.name,
        })),
      ];
    });
  };


  const handleRemoveArticle = (id: number) => {
    setArticles(prev => prev.filter(article => article.id !== id));
    setParamsArticlesRelated({
      withPaginate: false,
      filter: {
        only_active: true,
        category_id: selectedCategories?.value ?? undefined,
        section_id: selectedSection?.value ?? undefined,
        subsection_id: selectedSubsection?.value ?? undefined
      }
    });
  }

  return (
    <ModalDefault
      title={'Gerenciar Artigos Destaques'}
      show={show}
      onClose={handleClose}
      sizeModal={'xl'}
      showFooter={true}
      buttonText={isLoadingSubmit ? 'Salvando...' : 'Salvar'}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalArticleHighlightsInsert" data-testid="ModalArticleHighlightsInsert">
        <div className="d-flex align-items-center gap-2 justify-content-between">
          <Col md={5}>
            <Row>
              <Col>
                <FormGroup className="form-group">
                  <label htmlFor="category_id">CATEGORIA</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={categoriesOptions ?? []}
                    value={selectedCategories}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(errors?.category_id)}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedCategories(e ?? null);
                      setSelectedSection(null);
                      setArticlesRelatedOptions([]);
                      setSectionsOptions([]);
                    }}
                  />
                  <ErrorMessage
                    name={"Categoria"}
                    type={errors?.category_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup className="form-group">
                  <label htmlFor="section_id">SEÇÃO</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={sectionsOptions ?? []}
                    value={selectedSection}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(errors?.section_id)}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedSection(e ?? null);
                      setSelectedSubsection(null);
                      setSubsectionsOptions([]);
                      if (!e) {
                        setParamsArticlesRelated({ withPaginate: false, filter: { only_active: true, category_id: selectedCategories?.value } });
                      }
                    }}
                  />
                  <ErrorMessage
                    name={"Seção"}
                    type={errors?.section_id?.type}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup className="form-group">
                  <label htmlFor="subsection_id">SUBSEÇÃO</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={subsectionsOptions ?? []}
                    value={selectedSubsection}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(errors?.subsection_id)}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedSubsection(e ?? null);
                      if (!e) {
                        setParamsArticlesRelated({ withPaginate: false, filter: { only_active: true, section_id: selectedSection?.value } });
                      }
                    }}
                  />
                  <ErrorMessage
                    name={"Subseção"}
                    type={errors?.subsection_id?.type}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup className="form-group">
                  <label htmlFor="articles-related">ARTIGOS</label>
                  <ReactSelect
                    isMulti
                    isSearchable
                    isClearable
                    options={articlesRelatedOptions ?? []}
                    value={selectedRelatedArticles}
                    placeholder="Selecione"
                    className={`form-control p-0`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedRelatedArticles(e.map((item) => item) || []);
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Col>
          <Col md="auto">
            <Row className="h-100 px-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm btn-block my-4 my-auto"
                disabled={selectedRelatedArticles.length === 0}
                onClick={() => { handleAddArticle(); setSelectedRelatedArticles([]); }}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </Row>
          </Col>
          <Col md={5}>
            <Row className="h-100 py-3">
              <Col>
                <Card className={`card-dest h-100 ${onInvalid(errors?.highlights)}`}>
                  <Card.Body className="pb-3">
                    <div className="d-flex align-items-between flex-column h-100 gap-2">
                      <div className="h-100">
                        <h5>Artigos Destaques ({articles?.length})</h5>
                        {articles?.length > 0 ? (
                          <div className="overflow-y-auto pb-3 px-3" style={{ maxHeight: "250px" }}>
                            <div className="d-flex flex-column gap-3 mt-4">
                              {articles?.map((article) => (
                                <Card className="w-100" key={article.id}>
                                  <Card.Body>
                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                      <div>
                                        <p className="text-title text-xs">[{article.category_name}]</p>
                                        <p className="text-title mt-1">
                                          {article.title}
                                        </p>
                                      </div>

                                      <div>
                                        <button type="button" className="btn btn-link p-0 mb-0" onClick={() => handleRemoveArticle(article.id)}>
                                          <BvIcon name="trashDelete" width={18} height={18} variant="duo" />
                                        </button>
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                            <div ref={bottomRef} />
                          </div>
                        ) : (
                          <p className="text-muted text-sm my-3 mb-2">Nenhum artigo destacado</p>
                        )}
                      </div>
                      <div>
                        <ErrorMessage
                          name={"Artigos Destaques"}
                          type={errors?.highlights?.type}
                          min={1}
                          max={10}
                          isArray
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </div>
      </div>
    </ModalDefault>
  );
}

export default ModalArticleHighlightsInsert;
